/**
 * Tab "Đánh giá" — per-student bulk review form.
 * Uses React Hook Form useFieldArray. Pre-fills from existing sessionReviews.
 * Unsaved-changes warning via beforeunload event.
 */
import { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { toast } from 'sonner';
import { Save, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ReviewRow } from './review-row';
import { useBulkSaveReviews } from '@client/lib/queries/use-session-reviews';
import type { DashboardEnrollment } from '@client/lib/queries/use-session-dashboard';

// ---------------------------------------------------------------------------
// Form shape
// ---------------------------------------------------------------------------
interface ReviewField {
  enrollmentId: string;
  studentName: string;
  studentCode: string;
  participation: number;
  comprehension: number;
  behavior: number;
  comment: string;
}

interface ReviewForm {
  reviews: ReviewField[];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface TabReviewProps {
  classId: string;
  sessionId: string;
  enrollments: DashboardEnrollment[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function TabReview({ classId, sessionId, enrollments }: TabReviewProps) {
  const saveReviews = useBulkSaveReviews(classId, sessionId);

  const { control, handleSubmit, watch, reset, setValue, formState } = useForm<ReviewForm>({
    defaultValues: {
      reviews: enrollments.map((e) => ({
        enrollmentId: e.enrollmentId,
        studentName: e.studentName,
        studentCode: e.studentCode ?? '',
        participation: e.sessionReview?.participation ?? 3,
        comprehension: e.sessionReview?.comprehension ?? 3,
        behavior: e.sessionReview?.behavior ?? 3,
        comment: e.sessionReview?.comment ?? '',
      })),
    },
  });

  const { fields } = useFieldArray({ control, name: 'reviews' });

  // Re-initialise when enrollments change (e.g. dashboard refetch)
  useEffect(() => {
    reset({
      reviews: enrollments.map((e) => ({
        enrollmentId: e.enrollmentId,
        studentName: e.studentName,
        studentCode: e.studentCode ?? '',
        participation: e.sessionReview?.participation ?? 3,
        comprehension: e.sessionReview?.comprehension ?? 3,
        behavior: e.sessionReview?.behavior ?? 3,
        comment: e.sessionReview?.comment ?? '',
      })),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollments.length, sessionId]);

  // Unsaved-changes warning on page unload
  const isDirty = formState.isDirty;
  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (!isDirty) return;
      e.preventDefault();
      // Modern browsers show their own message; setting returnValue triggers dialog
      e.returnValue = 'Có thay đổi chưa lưu. Bạn có chắc muốn rời trang?';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const reviews = watch('reviews');

  async function onSubmit(data: ReviewForm) {
    try {
      await saveReviews.mutateAsync({
        sessionId,
        reviews: data.reviews.map((r) => ({
          enrollmentId: r.enrollmentId,
          participation: r.participation,
          comprehension: r.comprehension,
          behavior: r.behavior,
          comment: r.comment || undefined,
        })),
      });
      toast.success('Đã lưu đánh giá.');
      reset(data); // mark form clean after successful save
    } catch {
      toast.error('Lỗi khi lưu đánh giá. Vui lòng thử lại.');
    }
  }

  if (enrollments.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <Star className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">Lớp chưa có học viên để đánh giá.</p>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4 pb-24 md:pb-4">
      {/* Dirty indicator */}
      {isDirty && (
        <p className="text-xs text-muted-foreground">
          Có thay đổi chưa lưu.
        </p>
      )}

      {/* Per-student rows */}
      <div className="flex flex-col gap-3">
        {fields.map((field, index) => (
          <ReviewRow
            key={field.id}
            index={index}
            studentName={reviews[index]?.studentName ?? ''}
            studentCode={reviews[index]?.studentCode}
            participation={reviews[index]?.participation}
            comprehension={reviews[index]?.comprehension}
            behavior={reviews[index]?.behavior}
            comment={reviews[index]?.comment ?? ''}
            onParticipation={(v) => setValue(`reviews.${index}.participation`, v, { shouldDirty: true })}
            onComprehension={(v) => setValue(`reviews.${index}.comprehension`, v, { shouldDirty: true })}
            onBehavior={(v) => setValue(`reviews.${index}.behavior`, v, { shouldDirty: true })}
            onComment={(v) => setValue(`reviews.${index}.comment`, v, { shouldDirty: true })}
          />
        ))}
      </div>

      {/* Save action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-20 bg-background border-t border-border p-3 flex gap-2 md:static md:border-0 md:p-0 md:bg-transparent md:mt-4">
        <Button
          type="submit"
          className="flex-1 min-h-[44px]"
          disabled={saveReviews.isPending}
        >
          <Save className="mr-2 h-4 w-4" />
          {saveReviews.isPending ? 'Đang lưu...' : 'Lưu đánh giá'}
        </Button>
      </div>
    </form>
  );
}
